import { useEffect, useState } from "react";
import api from "../lib/api";
import { SignatureCard } from "../components/SignatureCard";
import { InstitutionCard } from "../components/institution/InstitutionCard";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { CreditCard, CheckCircle2, Loader2, Building2, ShieldCheck } from "lucide-react";

const STATUS_STYLE = {
  active: "bg-emerald-100 text-emerald-800",
  trial: "bg-sky-100 text-sky-800",
  cancelled: "bg-slate-200 text-slate-700",
  complimentary: "bg-violet-100 text-violet-800",
  institutional: "bg-sky-100 text-sky-800",
};

const fmt = (d) => (d === "lifetime" ? "Lifetime" : d ? new Date(d).toLocaleDateString() : "—");

const PLAN_OPTS = [
  { id: "monthly", name: "Monthly", price: "$10", per: "/month", note: "Billed monthly" },
  { id: "annual", name: "Annual", price: "$100", per: "/year", note: "2 months free" },
];

export default function Account() {
  const { user, refreshUser } = useAuth();
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  const [plan, setPlan] = useState(params.get("plan") === "annual" ? "annual" : "monthly");

  const load = () => api.get("/subscription").then(({ data }) => setSub(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const subscribe = async () => {
    setBusy(true);
    try {
      await api.post("/subscription/checkout", { plan });
      toast.success("Subscription activated");
      await load(); await refreshUser();
    } catch { toast.error("Could not process"); } finally { setBusy(false); }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await api.post("/subscription/cancel");
      toast.success("Subscription cancelled");
      await load(); await refreshUser();
    } catch { toast.error("Could not cancel"); } finally { setBusy(false); }
  };

  const status = sub?.status || "trial";

  return (
    <div data-testid="account-page" className="max-w-3xl">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">My Account</h1>
      <p className="text-slate-500 mt-1">Manage your profile and subscription.</p>

      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm mt-6">
        <h3 className="font-heading font-semibold text-slate-900">Profile</h3>
        <div className="grid sm:grid-cols-2 gap-4 mt-4 text-sm">
          <div><p className="text-slate-500">Name</p><p className="font-medium text-slate-900">{user?.name}</p></div>
          <div><p className="text-slate-500">Email</p><p className="font-medium text-slate-900">{user?.email}</p></div>
          <div><p className="text-slate-500">Clinic</p><p className="font-medium text-slate-900">{user?.clinic_name || "—"}</p></div>
          <div><p className="text-slate-500">Email verified</p><p className="font-medium">{user?.is_verified ? <span className="text-emerald-600">Yes</span> : <span className="text-amber-600">Pending</span>}</p></div>
        </div>
      </div>

      <InstitutionCard onChanged={() => { load(); refreshUser(); }} />

      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm mt-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h3 className="font-heading font-semibold text-slate-900">Subscription</h3>
          <span data-testid="subscription-status" className={`text-xs font-bold px-3 py-1 rounded-full uppercase ${STATUS_STYLE[status] || STATUS_STYLE.trial}`}>{status}</span>
        </div>

        <div className="mt-4 text-sm text-slate-600" data-testid="subscription-dates">
          {status === "trial" && <p>Free trial ends: <b>{fmt(sub?.trial_ends_at)}</b></p>}
          {status === "active" && sub?.plan === "admin" && <p>Administrator account — full access.</p>}
          {status === "active" && sub?.plan !== "admin" && <p>Plan: <b className="capitalize">{sub?.plan}</b> · renews on <b>{fmt(sub?.current_period_end)}</b></p>}
          {status === "cancelled" && sub?.current_period_end && <p>Cancelled — access continues until <b>{fmt(sub.current_period_end)}</b>. You will not be charged again.</p>}
          {status === "institutional" && <p>Institutional license from <b>{user?.org_name}</b> · valid {user?.institution_until?.startsWith("2099") ? <b>for life</b> : <>until <b>{fmt(user?.institution_until)}</b></>}</p>}
          {status === "complimentary" && <p>Complimentary access granted by DentiCision · valid until <b>{fmt(sub?.complimentary_until)}</b></p>}
        </div>

        {!["complimentary", "institutional"].includes(status) && (
          <div className="mt-5 rounded-xl border border-sky-200 bg-sky-50 p-6">
            {status !== "active" && (
              <div className="grid sm:grid-cols-2 gap-3">
                {PLAN_OPTS.map((p) => (
                  <button key={p.id} type="button" data-testid={`account-plan-${p.id}`} onClick={() => setPlan(p.id)}
                    className={`text-left rounded-lg border p-4 bg-white transition-colors ${plan === p.id ? "border-sky-600 ring-2 ring-sky-200" : "border-slate-200"}`}>
                    <p className="text-sm font-medium text-sky-700">{p.name}</p>
                    <p className="font-heading text-2xl font-bold text-slate-900">{p.price}<span className="text-sm font-normal text-slate-500">{p.per}</span></p>
                    <p className="text-xs text-slate-500">{p.note}</p>
                  </button>
                ))}
              </div>
            )}
            <ul className="mt-4 space-y-2 text-sm text-slate-700">
              {["Unlimited evaluations & cases", "Lab requests & referral PDFs", "All clinical modules & references", "Cancel anytime — access until end of paid period"].map((f) => (
                <li key={f} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> {f}</li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-3 mt-5">
              {status !== "active" ? (
                <Button onClick={subscribe} disabled={busy} data-testid="subscribe-btn" className="bg-sky-600 hover:bg-sky-700">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard className="w-4 h-4 mr-2" /> Subscribe — {plan === "annual" ? "$100/year" : "$10/month"}</>}
                </Button>
              ) : (
                <Button onClick={cancel} disabled={busy} variant="outline" data-testid="cancel-sub-btn" className="border-slate-300">Cancel subscription</Button>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-3">Renews automatically until cancelled. No partial refunds. See <Link to="/refund-policy" className="text-sky-700 underline">Cancellation & Refund Policy</Link> and <Link to="/terms" className="text-sky-700 underline">Terms</Link>.</p>
          </div>
        )}

        <div className="mt-4 flex items-start gap-2 text-xs text-slate-500 bg-slate-50 rounded-lg p-3">
          <Building2 className="w-4 h-4 shrink-0 mt-0.5" />
          <span>Billing will be processed by <b>Tap Payments</b> (mada, Apple Pay, Visa, Mastercard) with direct settlement to your Saudi bank account. Live billing is connected once merchant keys are added — this is currently a <b>demo activation</b>.</span>
        </div>

        {sub?.invoices?.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-semibold text-slate-900 mb-2">Invoices</h4>
            <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
              {sub.invoices.map((inv, i) => (
                <div key={i} className="flex justify-between px-4 py-2.5 text-sm">
                  <span className="text-slate-600">{new Date(inv.date).toLocaleDateString()}</span>
                  <span className="font-medium text-slate-900">${inv.amount} {inv.currency}</span>
                  <span className="text-emerald-600">{inv.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mt-6"><SignatureCard /></div>

      <p className="flex items-center gap-2 text-xs text-slate-400 mt-4"><ShieldCheck className="w-4 h-4" /> DentiCision is an evidence-based second opinion to assist the dentist and does not replace professional clinical judgment or physician consultation.</p>
    </div>
  );
}
